import { apiClient } from '../client';
import { ENDPOINTS } from '../endpoints';

export type NotificationPriority = 'low' | 'medium' | 'high';

export interface AppNotification {
  _id: string;
  user: string;
  type: string;
  title: string;
  message: string;
  priority: NotificationPriority;
  read: boolean;
  channels?: {
    email?: boolean;
    push?: boolean;
    sms?: boolean;
    inApp?: boolean;
  };
  sentChannels?: {
    email?: boolean;
    push?: boolean;
    sms?: boolean;
  };
  createdAt: string;
  updatedAt: string;
  readAt?: string;
  /** Structured context for the notification. The one concept / one source rule
   *  (AGENTS §3) means special-case UI keys off `metadata.event`, not a new
   *  `type` enum value. e.g. first-connection uses type `device_activity` +
   *  `metadata.event === 'first_connection'`. */
  metadata?: Record<string, unknown> | null;
  /** Optional deep-link the notification points at, e.g. "/operator/dashboard". */
  actionUrl?: string | null;
}

export interface NotificationsPagination {
  page: number;
  limit: number;
  total: number;
  pages: number;
}

export interface NotificationsResult {
  notifications: AppNotification[];
  pagination: NotificationsPagination;
  unreadCount: number;
}

interface NotificationsResponse {
  success: boolean;
  data: NotificationsResult;
}

export const notificationService = {
  getNotifications: async (params?: {
    page?: number;
    limit?: number;
  }): Promise<NotificationsResult> => {
    const response = await apiClient.get<NotificationsResponse>(ENDPOINTS.NOTIFICATIONS.GET_ALL, {
      params,
    });

    if (!response.data?.success || !response.data?.data) {
      throw new Error('Failed to load notifications');
    }

    return response.data.data;
  },

  markAsRead: async (id: string): Promise<void> => {
    await apiClient.patch(ENDPOINTS.NOTIFICATIONS.MARK_READ(id));
  },

  markAllAsRead: async (): Promise<void> => {
    await apiClient.patch(ENDPOINTS.NOTIFICATIONS.MARK_ALL_READ);
  },

  deleteNotification: async (id: string): Promise<void> => {
    await apiClient.delete(ENDPOINTS.NOTIFICATIONS.DELETE(id));
  },
};

export default notificationService;
